import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaFolderOpen,
  FaSitemap,
  FaLock,
  FaSyncAlt,
  FaUsers,
  FaSearch,
  FaDownload,
  FaTimes,
  FaFileAlt,
  FaTrashAlt,
} from "react-icons/fa";
import api from "../api";
import { useAuth } from "../AuthContext";
import { hasPermission, FILE_MANAGER_PERMISSION } from "../utils/permissions";
import useTranslationStructureGuard from "../hooks/useTranslationStructureGuard";
import resolveDownloadPath from "../utils/downloads";

const buildHierarchy = (folders) => {
  if (!Array.isArray(folders) || folders.length === 0) {
    return [];
  }

  const categories = new Map();

  folders.forEach((folder) => {
    const categorySlug = folder.categorySlug || folder.categoryName || "sem-categoria";
    const formSlug = folder.formSlug || folder.formName || "sem-processo";

    if (!categories.has(categorySlug)) {
      categories.set(categorySlug, {
        name: folder.categoryName || "Sem categoria",
        forms: new Map(),
      });
    }

    const categoryEntry = categories.get(categorySlug);
    categoryEntry.name = folder.categoryName || categoryEntry.name;

    if (!categoryEntry.forms.has(formSlug)) {
      categoryEntry.forms.set(formSlug, {
        name: folder.formName || "Processo",
        items: [],
      });
    }

    const formEntry = categoryEntry.forms.get(formSlug);
    formEntry.name = folder.formName || formEntry.name;
    formEntry.items.push(folder);
  });

  return Array.from(categories.entries())
    .map(([categorySlug, data]) => ({
      category: data.name,
      categorySlug,
      forms: Array.from(data.forms.entries())
        .map(([formSlug, formData]) => ({
          formName: formData.name,
          formSlug,
          items: formData.items.sort((a, b) => a.fieldName.localeCompare(b.fieldName, "pt-BR")),
        }))
        .sort((a, b) => a.formName.localeCompare(b.formName, "pt-BR")),
    }))
    .sort((a, b) => a.category.localeCompare(b.category, "pt-BR"));
};

const createEmptyOptions = () => ({ users: [], sectors: [] });

const normalizeAccessOptions = (rawOptions) => {
  if (!rawOptions || typeof rawOptions !== "object") {
    return createEmptyOptions();
  }

  const normalizeList = (list) => {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.filter((item) => item && typeof item === "object");
  };

  return {
    users: normalizeList(rawOptions.users),
    sectors: normalizeList(rawOptions.sectors),
  };
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "-";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const formatDateTime = (isoValue) => {
  if (!isoValue) return "-";
  try {
    const date = new Date(isoValue);
    if (Number.isNaN(date.getTime())) return isoValue;
    return date.toLocaleString();
  } catch {
    return isoValue;
  }
};

const normalizeSearchValue = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const matchesSearch = (source, query) => {
  if (!query) return true;
  return normalizeSearchValue(source).includes(normalizeSearchValue(query));
};

const TranslationSafeIconButton = React.forwardRef(function TranslationSafeIconButton(
  { icon: Icon, label, className = "", disabled, ...props },
  ref,
) {
  const normalizedLabel = typeof label === "string" ? label : "";

  return (
    <button
      {...props}
      ref={ref}
      disabled={disabled}
      className={className}
      data-translation-guard="true"
      data-label-text={normalizedLabel}
    >
      <span
        aria-hidden="true"
        data-structural-node="icon"
        data-structural-order="0"
        className="flex items-center"
      >
        {Icon ? <Icon /> : null}
      </span>
      <span
        data-structural-node="label"
        data-structural-order="1"
        data-default-label={normalizedLabel}
      >
        {normalizedLabel}
      </span>
    </button>
  );
});

const getRequestGroupTitle = (group) => {
  if (!group?.request) {
    return "Anexos sem solicitação";
  }
  if (group.request.label) {
    return group.request.label;
  }
  if (group.request.requestId && group.request.requester) {
    return `Solicitação ${group.request.requestId} — ${group.request.requester}`;
  }
  if (group.request.requestId) {
    return `Solicitação ${group.request.requestId}`;
  }
  if (group.request.requester) {
    return group.request.requester;
  }
  return group.request.raw || "Solicitação";
};

const getRequestGroupSubtitle = (group) => {
  if (!group?.request) {
    return "Arquivos enviados sem identificação da solicitação.";
  }
  const details = [];
  if (group.request.protocol) {
    details.push(`Protocolo ${group.request.protocol}`);
  }
  if (group.request.sector) {
    details.push(group.request.sector);
  }
  return details.join(" • ");
};

export default function FileManager({ refreshKey }) {
  const { token, user } = useAuth();
  const containerRef = useRef(null);
  const [folders, setFolders] = useState([]);
  const [options, setOptions] = useState(() => createEmptyOptions());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterTerm, setFilterTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [sectorSearch, setSectorSearch] = useState("");

  const [selectedCategorySlug, setSelectedCategorySlug] = useState("");
  const [selectedFormSlug, setSelectedFormSlug] = useState("");
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderSummary, setFolderSummary] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState("");
  const [fileSearch, setFileSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [downloadingKey, setDownloadingKey] = useState("");
  const [deletingKey, setDeletingKey] = useState("");
  const [deletingCategory, setDeletingCategory] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewerFolders, setViewerFolders] = useState([]);
  const [categoryEditor, setCategoryEditor] = useState(null);
  const [categoryMerge, setCategoryMerge] = useState(null);
  const [formEditor, setFormEditor] = useState(null);
  const [formMerge, setFormMerge] = useState(null);
  const [fieldEditor, setFieldEditor] = useState(null);
  const [structureSaving, setStructureSaving] = useState(false);
  const [structureError, setStructureError] = useState("");

  useTranslationStructureGuard(containerRef);

  const canManageAccess = useMemo(
    () => hasPermission(user, FILE_MANAGER_PERMISSION),
    [user],
  );

  const filteredUsers = useMemo(() => {
    const availableUsers = Array.isArray(options?.users) ? options.users : [];
    const term = userSearch.trim();
    if (!term) return availableUsers;
    return availableUsers.filter((userOption) =>
      matchesSearch(`${userOption.name || ""} ${userOption.email || ""}`, term),
    );
  }, [options?.users, userSearch]);

  const filteredSectors = useMemo(() => {
    const availableSectors = Array.isArray(options?.sectors) ? options.sectors : [];
    const term = sectorSearch.trim();
    if (!term) return availableSectors;
    return availableSectors.filter((sectorOption) =>
      matchesSearch(sectorOption.name || "", term),
    );
  }, [options?.sectors, sectorSearch]);

  const filteredUserIds = useMemo(
    () => filteredUsers.map((userOption) => String(userOption.id)),
    [filteredUsers],
  );

  const filteredSectorIds = useMemo(
    () => filteredSectors.map((sectorOption) => String(sectorOption.id)),
    [filteredSectors],
  );

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const folderRes = await api.get("/document-folders");

      if (!Array.isArray(folderRes.data)) {
        throw new Error("INVALID_DOCUMENT_FOLDERS_RESPONSE");
      }

      setFolders(folderRes.data);

      if (canManageAccess) {
        try {
          const optionRes = await api.get("/document-folders/access-options");
          setOptions(normalizeAccessOptions(optionRes.data));
        } catch (optionError) {
          console.error("Falha ao carregar opções de acesso", optionError);
          setOptions((prev) => normalizeAccessOptions(prev));
        }
      } else {
        setOptions(createEmptyOptions());
      }
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível carregar as pastas";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, canManageAccess]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(fileSearch.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [fileSearch]);

  useEffect(() => {
    if (!activeFolder) return;
    let cancelled = false;
    setFilesLoading(true);
    setFilesError("");

    const params = {
      q: debouncedSearch || undefined,
      year: yearFilter || undefined,
      month: monthFilter || undefined,
    };

    api
      .get(`/document-folders/${activeFolder.id}/files`, { params })
      .then((res) => {
        if (cancelled) return;
        setFolderFiles(res.data?.files || []);
        setFolderSummary(res.data?.summary || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        const message =
          status === 403
            ? "Você não tem permissão para visualizar os anexos desta pasta."
            : err.response?.data?.error || "Não foi possível carregar os anexos";
        setFilesError(message);
        setFolderFiles([]);
        setFolderSummary(null);
      })
      .finally(() => {
        if (!cancelled) {
          setFilesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeFolder, debouncedSearch, yearFilter, monthFilter]);

  useEffect(() => {
    if (!folderSummary) return;
    if (!yearFilter) {
      setMonthFilter("");
      return;
    }
    const months = folderSummary.monthsByYear?.[yearFilter] || [];
    if (!months.some((month) => month.value === monthFilter)) {
      setMonthFilter("");
    }
  }, [folderSummary, yearFilter, monthFilter]);

  const hierarchy = useMemo(() => buildHierarchy(folders), [folders]);

  const categoryOptions = useMemo(
    () =>
      hierarchy.map((category) => ({
        slug: category.categorySlug,
        name: category.category,
      })),
    [hierarchy],
  );

  const formMergeOptions = useMemo(() => {
    if (!formMerge) return [];
    const category = hierarchy.find((entry) => entry.categorySlug === formMerge.categorySlug);
    return category ? category.forms : [];
  }, [formMerge, hierarchy]);

  useEffect(() => {
    if (!categoryFilter) return;
    if (!categoryOptions.some((option) => option.slug === categoryFilter)) {
      setCategoryFilter("");
    }
  }, [categoryFilter, categoryOptions]);

  const filteredHierarchy = useMemo(() => {
    const term = filterTerm.trim().toLowerCase();

    return hierarchy
      .filter((category) => !categoryFilter || category.categorySlug === categoryFilter)
      .map((category) => {
        if (!term) return category;
        const filteredForms = category.forms
          .map((form) => {
            const filteredItems = form.items.filter((folder) => {
              const haystack = [
                category.category,
                form.formName,
                folder.fieldName,
                folder.basePath,
              ]
                .join(" ")
                .toLowerCase();
              return haystack.includes(term);
            });
            if (filteredItems.length === 0) return null;
            return { ...form, items: filteredItems };
          })
          .filter(Boolean);

        if (filteredForms.length === 0) return null;
        return { ...category, forms: filteredForms };
      })
      .filter(Boolean);
  }, [hierarchy, filterTerm, categoryFilter]);

  useEffect(() => {
    if (filteredHierarchy.length === 0) {
      if (selectedCategorySlug || selectedFormSlug) {
        setSelectedCategorySlug("");
        setSelectedFormSlug("");
      }
      return;
    }

    const categorySlugs = filteredHierarchy.map((category) => category.categorySlug);

    if (!categorySlugs.includes(selectedCategorySlug)) {
      const firstCategory = filteredHierarchy[0];
      setSelectedCategorySlug(firstCategory.categorySlug);
      setSelectedFormSlug(firstCategory.forms[0]?.formSlug || "");
      return;
    }

    const category = filteredHierarchy.find((item) => item.categorySlug === selectedCategorySlug);
    const formSlugs = (category?.forms || []).map((form) => form.formSlug);

    if (!formSlugs.includes(selectedFormSlug)) {
      setSelectedFormSlug(formSlugs[0] || "");
    }
  }, [filteredHierarchy, selectedCategorySlug, selectedFormSlug]);

  const activeCategory = useMemo(
    () => filteredHierarchy.find((category) => category.categorySlug === selectedCategorySlug) || null,
    [filteredHierarchy, selectedCategorySlug],
  );

  const activeProcess = useMemo(() => {
    if (!activeCategory) return null;
    return activeCategory.forms.find((form) => form.formSlug === selectedFormSlug) || null;
  }, [activeCategory, selectedFormSlug]);

  const canMergeActiveProcess = useMemo(() => {
    if (!activeCategory || !activeProcess) return false;
    return activeCategory.forms.some((form) => form.formSlug !== activeProcess.formSlug);
  }, [activeCategory, activeProcess]);

  const openAccessModal = (folder) => {
    if (!canManageAccess) return;
    setEditing({
      id: folder.id,
      title: `${folder.categoryName} › ${folder.formName} › ${folder.fieldName}`,
      userIds: folder.allowedUsers?.map((user) => String(user.id)) || [],
      sectorIds: folder.allowedSectors?.map((sector) => String(sector.id)) || [],
    });
    setUserSearch("");
    setSectorSearch("");
  };

  const closeModal = () => {
    setEditing(null);
    setSaving(false);
    setUserSearch("");
    setSectorSearch("");
  };

  const openViewer = (folder, relatedFolders = []) => {
    if (!folder) return;
    setActiveFolder(folder);
    setViewerFolders(relatedFolders.length > 0 ? relatedFolders : [folder]);
    setFolderFiles([]);
    setFolderSummary(null);
    setFilesError("");
    setFileSearch("");
    setYearFilter("");
    setMonthFilter("");
    setExpandedGroups({});
    setDownloadingKey("");
    setDeletingKey("");
  };

  const openProcessViewer = (form, { manageAccess = false } = {}) => {
    if (!form?.items?.length) return;
    const sortedFolders = form.items
      .slice()
      .sort((a, b) => a.fieldName.localeCompare(b.fieldName, "pt-BR"));
    const [firstFolder] = sortedFolders;
    if (!firstFolder) return;
    if (manageAccess && canManageAccess) {
      closeViewer();
      openAccessModal(firstFolder);
      return;
    }
    openViewer(firstFolder, sortedFolders);
  };

  const changeViewerFolder = (folderId) => {
    if (!folderId || viewerFolders.length === 0) return;
    const nextFolder = viewerFolders.find((item) => String(item.id) === String(folderId));
    if (!nextFolder || nextFolder.id === activeFolder?.id) {
      return;
    }
    setActiveFolder(nextFolder);
    setExpandedGroups({});
    setDownloadingKey("");
    setDeletingKey("");
  };

  const closeViewer = () => {
    setActiveFolder(null);
    setViewerFolders([]);
    setFolderFiles([]);
    setFolderSummary(null);
    setFilesError("");
    setFileSearch("");
    setYearFilter("");
    setMonthFilter("");
    setExpandedGroups({});
    setDownloadingKey("");
    setDeletingKey("");
  };

  const toggleEditingSelection = (key, value) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const current = prev[key] || [];
      const exists = current.includes(value);
      const nextValues = exists ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [key]: nextValues };
    });
  };

  const toggleAllEditingSelections = (key, values) => {
    if (!values || values.length === 0) return;
    setEditing((prev) => {
      if (!prev) return prev;
      const current = prev[key] || [];
      const allSelected = values.every((value) => current.includes(value));
      const nextValues = allSelected
        ? current.filter((value) => !values.includes(value))
        : Array.from(new Set([...current, ...values]));
      return { ...prev, [key]: nextValues };
    });
  };

  const handleSaveAccess = async () => {
    if (!editing || !canManageAccess) return;
    setSaving(true);
    try {
      const payload = {
        userIds: editing.userIds.map((id) => Number(id)),
        sectorIds: editing.sectorIds.map((id) => Number(id)),
      };
      const res = await api.put(`/document-folders/${editing.id}/access`, payload);
      const updated = res.data;
      setFolders((prev) => prev.map((folder) => (folder.id === updated.id ? updated : folder)));
      closeModal();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível salvar o acesso";
      alert(message);
      setSaving(false);
    }
  };

  const availableYears = folderSummary?.years || [];
  const availableMonths = useMemo(() => {
    if (!folderSummary || !yearFilter) return [];
    return folderSummary.monthsByYear?.[yearFilter] || [];
  }, [folderSummary, yearFilter]);

  const requestGroups = folderSummary?.requestGroups || [];

  useEffect(() => {
    if (!activeFolder) {
      setExpandedGroups({});
      return;
    }

    if (!requestGroups || requestGroups.length === 0) {
      setExpandedGroups({});
      return;
    }

    setExpandedGroups((prev) => {
      const next = {};
      requestGroups.forEach((group) => {
        const key = group.key || group.request?.raw || "sem-solicitacao";
        const hasPrevious = Object.prototype.hasOwnProperty.call(prev, key);
        next[key] = hasPrevious ? prev[key] : false;
      });
      return next;
    });
  }, [activeFolder, requestGroups]);

  const toggleGroupExpansion = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownloadFile = async (file) => {
    if (!file?.downloadPath) return;
    if (!token) {
      alert("Faça login para baixar este arquivo.");
      return;
    }
    if (downloadingKey === file.relativePath) return;

    setDownloadingKey(file.relativePath);
    try {
      const requestPath = resolveDownloadPath(file.downloadPath);
      const response = await api.get(requestPath, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = file.name || "arquivo";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Erro ao baixar arquivo", err);
      alert("Não foi possível baixar este arquivo.");
    } finally {
      setDownloadingKey("");
    }
  };

  const handleDeleteFile = async (file) => {
    if (!file?.downloadPath) return;
    if (!token) {
      alert("Faça login para excluir este arquivo.");
      return;
    }
    if (!canManageAccess) {
      alert("Você não tem permissão para excluir anexos.");
      return;
    }
    if (deletingKey === file.relativePath) return;

    const confirmed = window.confirm(
      "Tem certeza que deseja mover este arquivo para a lixeira?",
    );
    if (!confirmed) {
      return;
    }

    setDeletingKey(file.relativePath);
    try {
      const requestPath = resolveDownloadPath(file.downloadPath);
      await api.delete(requestPath);
      setFolderFiles((prev) => prev.filter((item) => item.relativePath !== file.relativePath));
      setFolderSummary((prev) => {
        if (!prev) return prev;
        const totalFiles = Math.max((prev.totalFiles || 0) - 1, 0);
        const totalMatching = Math.max((prev.totalMatching || 0) - 1, 0);
        return { ...prev, totalFiles, totalMatching };
      });
    } catch (err) {
      console.error("Erro ao excluir arquivo", err);
      alert("Não foi possível mover este arquivo para a lixeira.");
    } finally {
      setDeletingKey("");
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!canManageAccess) return;
    const slug = category?.categorySlug || "";
    if (!slug) {
      alert("Não foi possível identificar a categoria selecionada.");
      return;
    }
    if (deletingCategory === slug) return;

    const confirmed = window.confirm(
      `Tem certeza que deseja mover a categoria "${category.category}" para a lixeira? Todos os anexos associados serão removidos.`,
    );
    if (!confirmed) {
      return;
    }

    setDeletingCategory(slug);
    try {
      const res = await api.delete(`/document-folders/category/${encodeURIComponent(slug)}`);
      const message = res.data?.message || "Categoria movida para a lixeira.";
      alert(message);
      await loadData();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível excluir a categoria.";
      alert(message);
    } finally {
      setDeletingCategory("");
    }
  };

  const openCategoryRename = (category) => {
    if (!canManageAccess || !category?.categorySlug) return;
    setStructureError("");
    setStructureSaving(false);
    setCategoryEditor({
      slug: category.categorySlug,
      originalName: category.category,
      name: category.category,
    });
  };

  const openCategoryMerge = (category) => {
    if (!canManageAccess || !category?.categorySlug) return;
    setStructureError("");
    setStructureSaving(false);
    setCategoryMerge({
      slug: category.categorySlug,
      originalName: category.category,
      targetSlug: "",
      name: category.category,
    });
  };

  const closeCategoryEditor = () => {
    setCategoryEditor(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const closeCategoryMerge = () => {
    setCategoryMerge(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitCategoryRename = async () => {
    if (!categoryEditor || !canManageAccess) return;
    const name = categoryEditor.name?.trim();
    if (!name) {
      setStructureError("Informe o nome da categoria.");
      return;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const res = await api.put(`/document-folders/category/${encodeURIComponent(categoryEditor.slug)}`, {
        name,
      });
      const message = res.data?.message || "Categoria atualizada com sucesso.";
      const nextSlug = res.data?.slug || categoryEditor.slug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(nextSlug);
      setSelectedFormSlug("");
      closeCategoryEditor();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível atualizar a categoria.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const submitCategoryMerge = async () => {
    if (!categoryMerge || !canManageAccess) return;
    const targetSlug = categoryMerge.targetSlug;
    if (!targetSlug) {
      setStructureError("Selecione a categoria de destino.");
      return;
    }
    if (targetSlug === categoryMerge.slug) {
      setStructureError("Escolha uma categoria diferente para mesclar.");
      return;
    }

    const payload = { slug: targetSlug };
    const name = categoryMerge.name?.trim();
    if (name) {
      payload.name = name;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const res = await api.put(`/document-folders/category/${encodeURIComponent(categoryMerge.slug)}`, payload);
      const message = res.data?.message || "Categorias mescladas com sucesso.";
      const nextSlug = res.data?.slug || targetSlug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(nextSlug);
      setSelectedFormSlug("");
      closeCategoryMerge();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível mesclar as categorias.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const openFormRename = (category, form) => {
    if (!canManageAccess || !category?.categorySlug || !form?.formSlug) return;
    setStructureError("");
    setStructureSaving(false);
    setFormEditor({
      categorySlug: category.categorySlug,
      formSlug: form.formSlug,
      originalName: form.formName,
      name: form.formName,
    });
  };

  const closeFormEditor = () => {
    setFormEditor(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitFormRename = async () => {
    if (!formEditor || !canManageAccess) return;
    const name = formEditor.name?.trim();
    if (!name) {
      setStructureError("Informe o novo nome do processo.");
      return;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const endpoint = `/document-folders/category/${encodeURIComponent(formEditor.categorySlug)}/forms/${encodeURIComponent(
        formEditor.formSlug,
      )}`;
      const res = await api.put(endpoint, { name });
      const message = res.data?.message || "Processo atualizado com sucesso.";
      const nextSlug = res.data?.form?.slug || formEditor.formSlug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(formEditor.categorySlug);
      setSelectedFormSlug(nextSlug);
      closeFormEditor();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível atualizar o processo.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const openFormMerge = (category, form) => {
    if (!canManageAccess || !category?.categorySlug || !form?.formSlug) return;
    setStructureError("");
    setStructureSaving(false);
    setFormMerge({
      categorySlug: category.categorySlug,
      formSlug: form.formSlug,
      originalName: form.formName,
      targetSlug: "",
      name: form.formName,
    });
  };

  const closeFormMerge = () => {
    setFormMerge(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitFormMerge = async () => {
    if (!formMerge || !canManageAccess) return;
    const targetSlug = formMerge.targetSlug;
    if (!targetSlug) {
      setStructureError("Selecione o processo de destino.");
      return;
    }
    if (targetSlug === formMerge.formSlug) {
      setStructureError("Escolha um processo diferente para mesclar.");
      return;
    }

    const payload = { slug: targetSlug };
    const name = formMerge.name?.trim();
    if (name) {
      payload.name = name;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const endpoint = `/document-folders/category/${encodeURIComponent(formMerge.categorySlug)}/forms/${encodeURIComponent(
        formMerge.formSlug,
      )}`;
      const res = await api.put(endpoint, payload);
      const message = res.data?.message || "Processos mesclados com sucesso.";
      const nextSlug = res.data?.form?.slug || targetSlug;
      alert(message);
      await loadData();
      setSelectedCategorySlug(formMerge.categorySlug);
      setSelectedFormSlug(nextSlug);
      closeFormMerge();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível mesclar os processos.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const openFieldRename = (folder) => {
    if (!canManageAccess || !folder?.id) return;
    setStructureError("");
    setStructureSaving(false);
    setFieldEditor({
      id: folder.id,
      originalName: folder.fieldName,
      name: folder.fieldName,
    });
  };

  const closeFieldEditor = () => {
    setFieldEditor(null);
    setStructureSaving(false);
    setStructureError("");
  };

  const submitFieldRename = async () => {
    if (!fieldEditor || !canManageAccess) return;
    const name = fieldEditor.name?.trim();
    if (!name) {
      setStructureError("Informe o novo nome do campo.");
      return;
    }

    setStructureSaving(true);
    setStructureError("");

    try {
      const res = await api.put(`/document-folders/${fieldEditor.id}/names`, { fieldName: name });
      const updated = res.data;
      setFolders((prev) => prev.map((folder) => (folder.id === updated.id ? updated : folder)));
      setViewerFolders((prev) =>
        prev.map((folder) => (folder.id === updated.id ? { ...folder, ...updated } : folder)),
      );
      setActiveFolder((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      alert("Campo atualizado com sucesso.");
      closeFieldEditor();
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível atualizar o campo.";
      setStructureError(message);
      setStructureSaving(false);
    }
  };

  const renderProcessCard = (form) => {
    const folders = form.items
      .slice()
      .sort((a, b) => a.fieldName.localeCompare(b.fieldName, "pt-BR"));
    const totalFields = folders.length;
    const restrictedCount = folders.filter(
      (folder) =>
        (folder.allowedUsers && folder.allowedUsers.length > 0) ||
        (folder.allowedSectors && folder.allowedSectors.length > 0),
    ).length;
    const hasFolders = totalFields > 0;

    const restrictionMessage = !hasFolders
      ? "Nenhum campo disponível neste processo."
      : restrictedCount === 0
      ? "Nenhuma restrição explícita configurada (visível apenas para perfis com gestão de arquivos/processos)."
      : `${restrictedCount} ${restrictedCount === 1 ? "campo com restrição" : "campos com restrições"} de acesso.`;

    const handleOpenProcess = () => {
      if (!hasFolders) return;
      openProcessViewer(form);
    };

    const handleKeyDown = (event) => {
      if (!hasFolders) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openProcessViewer(form);
      }
    };

    return (
      <div
        role="button"
        tabIndex={hasFolders ? 0 : -1}
        onClick={handleOpenProcess}
        onKeyDown={handleKeyDown}
        aria-disabled={!hasFolders}
        className={`w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
          hasFolders ? "cursor-pointer hover:border-blue-300 hover:shadow-md" : "cursor-not-allowed opacity-75"
        }`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-500">
              <FaFolderOpen />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-slate-700">Anexos do Processo</span>
              <span className="text-xs text-slate-500">
                {totalFields} {totalFields === 1 ? "campo configurado" : "campos configurados"}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <FaUsers className="text-blue-500" />
            <span>{restrictionMessage}</span>
          </div>
          <span className="text-xs font-semibold text-indigo-600">
            {hasFolders
              ? "Clique para visualizar anexos por solicitação."
              : "Cadastre campos com anexos para habilitar esta visualização."}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="space-y-6">
      <header className="rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-500 p-6 text-white shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Gerenciador de Arquivos</h1>
            <p className="text-sm text-blue-100">
              Navegue por categoria e processo para encontrar anexos agrupados por solicitação.
            </p>
          </div>
          <TranslationSafeIconButton
            type="button"
            onClick={loadData}
            icon={FaSyncAlt}
            label="Atualizar"
            className="inline-flex items-center gap-2 rounded-full border border-white/50 bg-white/20 px-4 py-2 text-sm font-semibold backdrop-blur transition hover:bg-white/30"
          />
        </div>
      </header>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-lg lg:max-w-2xl">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filterTerm}
              onChange={(event) => setFilterTerm(event.target.value)}
              placeholder="Buscar por categoria, formulário ou campo"
              className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-56"
          >
            <option value="">Todas as categorias</option>
            {categoryOptions.map((category) => (
              <option key={category.slug} value={category.slug}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs text-slate-500">
          Processos encontrados: {filteredHierarchy.reduce((count, category) => count + category.forms.length, 0)}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/80 py-16 text-slate-500 shadow-sm">
          <FaSitemap className="mr-2 animate-spin text-xl" />
          Carregando estrutura de pastas...
        </div>
      ) : filteredHierarchy.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/80 py-16 text-center text-slate-500 shadow-sm">
          <FaFolderOpen className="mx-auto mb-2 text-3xl text-slate-400" />
          Nenhum anexo foi organizado ainda. Envie um arquivo em um processo para gerar uma nova pasta automática.
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(280px,340px)_minmax(0,1fr)]">
          <aside className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="text-sm font-semibold text-slate-600">Categorias</h2>
                <p className="text-xs text-slate-400">Selecione uma categoria para visualizar seus processos.</p>
              </div>
              <nav className="flex flex-col divide-y divide-slate-100">
                {filteredHierarchy.map((category) => {
                  const isActive = category.categorySlug === selectedCategorySlug;
                  const totalFields = category.forms.reduce(
                    (total, form) => total + form.items.length,
                    0,
                  );

                  const handleCategoryClick = () => {
                    setSelectedCategorySlug(category.categorySlug);
                    const firstForm = category.forms[0];
                    setSelectedFormSlug(firstForm ? firstForm.formSlug : "");
                  };

                  return (
                    <div
                      key={category.categorySlug}
                      className={`flex flex-col gap-2 px-4 py-3 text-left transition sm:flex-row sm:items-center sm:justify-between ${
                        isActive ? "bg-blue-50 text-blue-600" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={handleCategoryClick}
                        className="flex min-w-0 flex-1 items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        <div className="flex min-w-0 flex-col text-left">
                          <span className="break-words text-sm font-semibold leading-tight">{category.category}</span>
                          <span className="text-xs text-slate-400">
                            {category.forms.length} {category.forms.length === 1 ? "processo" : "processos"}
                          </span>
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            isActive ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {totalFields}
                        </span>
                      </button>
                      {canManageAccess && (
                        <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:flex-nowrap">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              openCategoryRename(category);
                            }}
                            className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"
                          >
                            Renomear
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              openCategoryMerge(category);
                            }}
                            className="rounded-full border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-blue-300 hover:text-blue-600"
                          >
                            Mesclar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </aside>
          <section className="space-y-6">
            {activeCategory ? (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-700">{activeCategory.category}</h2>
                    <p className="text-xs text-slate-500">
                      Escolha um processo para visualizar anexos agrupados por solicitação.
                    </p>
                  </div>
                  {canManageAccess && activeCategory?.categorySlug && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openCategoryRename(activeCategory)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                      >
                        Renomear
                      </button>
                      <button
                        type="button"
                        onClick={() => openCategoryMerge(activeCategory)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                      >
                        Mesclar
                      </button>
                      <TranslationSafeIconButton
                        type="button"
                        onClick={() => handleDeleteCategory(activeCategory)}
                        disabled={deletingCategory === activeCategory.categorySlug}
                        icon={FaTrashAlt}
                        label={
                          deletingCategory === activeCategory.categorySlug ? "Excluindo..." : "Excluir categoria"
                        }
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-red-200 ${
                          deletingCategory === activeCategory.categorySlug
                            ? "cursor-not-allowed border-red-200 bg-red-100 text-red-500"
                            : "border-red-500 text-red-600 hover:bg-red-50"
                        }`}
                      />
                    </div>
                  )}
                </div>

                {activeCategory.forms.length > 0 ? (
                  <>
                    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <FaSitemap className="text-blue-500" />
                          <h3 className="text-base font-semibold text-slate-600">Processos</h3>
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                          {activeCategory.forms.length} {activeCategory.forms.length === 1 ? "processo" : "processos"}
                        </span>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {activeCategory.forms.map((form) => {
                          const isProcessActive = form.formSlug === selectedFormSlug;
                          return (
                            <button
                              key={`${activeCategory.categorySlug}-${form.formSlug}`}
                              type="button"
                              onClick={() => setSelectedFormSlug(form.formSlug)}
                              className={`group inline-flex items-center justify-between rounded-full border px-3 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                                isProcessActive
                                  ? "border-blue-500 bg-blue-50 text-blue-600 shadow-sm"
                                  : "border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600"
                              }`}
                            >
                              <span>{form.formName}</span>
                              <span
                                className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  isProcessActive ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                {form.items.length}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {activeProcess ? (
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-700">{activeProcess.formName}</h3>
                          {canManageAccess && (
                            <>
                              <button
                                type="button"
                                onClick={() => openFormRename(activeCategory, activeProcess)}
                                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                              >
                                Renomear processo
                              </button>
                              <button
                                type="button"
                                onClick={() => openFormMerge(activeCategory, activeProcess)}
                                disabled={!canMergeActiveProcess}
                                className={`rounded-full border px-3 py-1 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                                  canMergeActiveProcess
                                    ? "border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600"
                                    : "cursor-not-allowed border-slate-200 text-slate-400"
                                }`}
                              >
                                Mesclar processo
                              </button>
                              <button
                                type="button"
                                onClick={() => openProcessViewer(activeProcess, { manageAccess: true })}
                                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                              >
                                Gerenciar acesso
                              </button>
                            </>
                          )}
                        </div>
                        {activeProcess.items.length > 0 ? (
                          <div className="max-w-3xl">{renderProcessCard(activeProcess)}</div>
                        ) : (
                          <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                            Nenhum campo disponível para este processo.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                        Selecione um processo para visualizar as pastas disponíveis.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                    Nenhum processo cadastrado nesta categoria.
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500 shadow-sm">
                Selecione uma categoria para visualizar os processos disponíveis.
              </div>
            )}
          </section>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-800">Gerenciar acesso</h2>
              <p className="text-sm text-slate-500">Defina quem pode baixar arquivos desta pasta.</p>
              <p className="mt-3 text-sm font-medium text-slate-700">{editing.title}</p>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Usuários autorizados</h3>
                    <p className="text-xs text-slate-500">Selecione os usuários que poderão baixar os arquivos.</p>
                  </div>
                  {filteredUsers.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleAllEditingSelections("userIds", filteredUserIds)}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                      {filteredUserIds.every((value) => editing.userIds.includes(value))
                        ? "Limpar seleção"
                        : "Selecionar visíveis"}
                    </button>
                  )}
                </div>
                <div className="mt-3">
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                      placeholder="Filtrar por nome ou e-mail"
                      className="w-full rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-600 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
                <div className="mt-4 flex-1">
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((userOption) => {
                        const value = String(userOption.id);
                        const checked = editing.userIds.includes(value);
                        return (
                          <label
                            key={userOption.id}
                            className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:border-blue-200"
                          >
                            <span className="flex-1">
                              <span className="block font-medium text-slate-700">{userOption.name}</span>
                              <span className="block text-xs text-slate-500">{userOption.email}</span>
                            </span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEditingSelection("userIds", value)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                          </label>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                        Nenhum usuário encontrado.
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-400">
                  Se nenhum usuário for selecionado, todos os usuários com login poderão acessar.
                </p>
              </div>
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Setores autorizados</h3>
                    <p className="text-xs text-slate-500">Limite o acesso por setores da organização.</p>
                  </div>
                  {filteredSectors.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleAllEditingSelections("sectorIds", filteredSectorIds)}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                      {filteredSectorIds.every((value) => editing.sectorIds.includes(value))
                        ? "Limpar seleção"
                        : "Selecionar visíveis"}
                    </button>
                  )}
                </div>
                <div className="mt-3">
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={sectorSearch}
                      onChange={(event) => setSectorSearch(event.target.value)}
                      placeholder="Filtrar por nome do setor"
                      className="w-full rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-600 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
                <div className="mt-4 flex-1">
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {filteredSectors.length > 0 ? (
                      filteredSectors.map((sectorOption) => {
                        const value = String(sectorOption.id);
                        const checked = editing.sectorIds.includes(value);
                        return (
                          <label
                            key={sectorOption.id}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:border-blue-200"
                          >
                            <span className="flex-1 font-medium text-slate-700">{sectorOption.name}</span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEditingSelection("sectorIds", value)}
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                          </label>
                        );
                      })
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                        Nenhum setor encontrado.
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-400">
                  Combine restrições de usuários e setores para personalizar o acesso.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveAccess}
                disabled={saving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  saving ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {saving ? "Salvando..." : "Salvar acesso"}
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryEditor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-800">Renomear categoria</h2>
            <p className="mt-2 text-sm text-slate-500">Atualize o nome exibido para os processos desta categoria.</p>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Novo nome</label>
              <input
                type="text"
                value={categoryEditor.name}
                onChange={(event) =>
                  setCategoryEditor((prev) =>
                    prev ? { ...prev, name: event.target.value } : prev,
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Nome da categoria"
              />
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCategoryEditor}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitCategoryRename}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryMerge && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-800">Mesclar categorias</h2>
            <p className="mt-2 text-sm text-slate-500">
              Escolha a categoria de destino para unificar todos os processos e campos.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500">Categoria de destino</label>
                <select
                  value={categoryMerge.targetSlug}
                  onChange={(event) =>
                    setCategoryMerge((prev) =>
                      prev
                        ? {
                            ...prev,
                            targetSlug: event.target.value,
                            name:
                              categoryOptions.find((option) => option.slug === event.target.value)?.name ??
                              prev.name,
                          }
                        : prev,
                    )
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Selecione uma categoria</option>
                  {categoryOptions
                    .filter((option) => option.slug !== categoryMerge.slug)
                    .map((option) => (
                      <option key={option.slug} value={option.slug}>
                        {option.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500">Nome após a mesclagem</label>
                <input
                  type="text"
                  value={categoryMerge.name}
                  onChange={(event) =>
                    setCategoryMerge((prev) =>
                      prev ? { ...prev, name: event.target.value } : prev,
                    )
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome final da categoria"
                />
              </div>
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCategoryMerge}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitCategoryMerge}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Mesclando..." : "Mesclar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {formEditor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-800">Renomear processo</h2>
            <p className="mt-2 text-sm text-slate-500">Defina um novo nome para todos os campos deste processo.</p>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Novo nome</label>
              <input
                type="text"
                value={formEditor.name}
                onChange={(event) =>
                  setFormEditor((prev) =>
                    prev ? { ...prev, name: event.target.value } : prev,
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Nome do processo"
              />
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFormEditor}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitFormRename}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {formMerge && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-800">Mesclar processos</h2>
            <p className="mt-2 text-sm text-slate-500">
              Escolha o processo que receberá os campos e defina o nome final após a unificação.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500">Processo de destino</label>
                <select
                  value={formMerge.targetSlug}
                  onChange={(event) =>
                    setFormMerge((prev) =>
                      prev
                        ? {
                            ...prev,
                            targetSlug: event.target.value,
                            name:
                              formMergeOptions.find((option) => option.formSlug === event.target.value)?.formName ??
                              prev.name,
                          }
                        : prev,
                    )
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Selecione um processo</option>
                  {formMergeOptions
                    .filter((option) => option.formSlug !== formMerge.formSlug)
                    .map((option) => (
                      <option key={option.formSlug} value={option.formSlug}>
                        {option.formName}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500">Nome após a mesclagem</label>
                <input
                  type="text"
                  value={formMerge.name}
                  onChange={(event) =>
                    setFormMerge((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome final do processo"
                />
              </div>
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFormMerge}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitFormMerge}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Mesclando..." : "Mesclar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fieldEditor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-800">Renomear campo</h2>
            <p className="mt-2 text-sm text-slate-500">Altere o nome do campo sem perder os anexos já catalogados.</p>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Novo nome</label>
              <input
                type="text"
                value={fieldEditor.name}
                onChange={(event) =>
                  setFieldEditor((prev) =>
                    prev ? { ...prev, name: event.target.value } : prev,
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Nome do campo"
              />
            </div>
            {structureError && (
              <p className="mt-3 text-sm text-red-600">{structureError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFieldEditor}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitFieldRename}
                disabled={structureSaving}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                  structureSaving ? "cursor-not-allowed bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {structureSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
          <div className="flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {activeFolder.categoryName}
                    </span>
                    <h2 className="text-xl font-semibold text-slate-800">{activeFolder.formName}</h2>
                    <p className="text-sm text-slate-500">Anexos organizados por solicitação e campo.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {canManageAccess && (
                      <>
                        <button
                          type="button"
                          onClick={() => openFieldRename(activeFolder)}
                          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
                        >
                          Renomear campo
                        </button>
                        <TranslationSafeIconButton
                          type="button"
                          onClick={() => openAccessModal(activeFolder)}
                          icon={FaLock}
                          label="Gerenciar acesso do campo"
                          className="inline-flex items-center gap-2 rounded-full border border-blue-500 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                        />
                      </>
                    )}
                    <button
                      type="button"
                      onClick={closeViewer}
                      className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-100"
                    >
                    <FaTimes />
                  </button>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-600">Campo selecionado:</span>
                  {viewerFolders.length > 1 ? (
                    <select
                      value={String(activeFolder.id)}
                      onChange={(event) => changeViewerFolder(event.target.value)}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      {viewerFolders.map((folderOption) => (
                        <option key={folderOption.id} value={String(folderOption.id)}>
                          {folderOption.fieldName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {activeFolder.fieldName}
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-400">
                  {viewerFolders.length} {viewerFolders.length === 1 ? "campo neste processo" : "campos neste processo"}
                </span>
              </div>
            </div>
            <div className="grid gap-4 border-b border-slate-100 p-6 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={fileSearch}
                  onChange={(event) => setFileSearch(event.target.value)}
                  placeholder="Buscar por nome, protocolo ou solicitante"
                  className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
              <select
                value={yearFilter}
                onChange={(event) => setYearFilter(event.target.value)}
                className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">Todos os anos</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select
                value={monthFilter}
                onChange={(event) => setMonthFilter(event.target.value)}
                disabled={!yearFilter}
                className="rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
              >
                <option value="">Todos os meses</option>
                {availableMonths.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between px-6 pb-4 text-xs text-slate-500">
              <span>Total catalogado: {folderSummary?.totalFiles ?? 0}</span>
              <span>Exibindo: {folderSummary?.totalMatching ?? folderFiles.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-6 pt-0">
              {filesError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {filesError}
                </div>
              ) : filesLoading ? (
                <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white py-12 text-slate-500">
                  <FaSitemap className="mr-2 animate-spin" /> Carregando anexos...
                </div>
              ) : folderSummary?.totalFiles === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500">
                  Nenhum anexo foi salvo nesta pasta ainda.
                </div>
              ) : requestGroups.length > 0 ? (
                <div className="space-y-3">
                  {requestGroups.map((group) => {
                    const key = group.key || group.request?.raw || "sem-solicitacao";
                    const groupTitle = getRequestGroupTitle(group);
                    const groupSubtitle = getRequestGroupSubtitle(group);
                    const isExpanded = expandedGroups[key] ?? false;
                    return (
                      <div key={key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <button
                          type="button"
                          onClick={() => toggleGroupExpansion(key)}
                          className="flex w-full items-center justify-between gap-3 bg-slate-50 px-4 py-3 text-left transition hover:bg-slate-100"
                        >
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-slate-700">{groupTitle}</p>
                            {groupSubtitle && <p className="text-xs text-slate-500">{groupSubtitle}</p>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span>Total: {group.totalFiles}</span>
                            <span>Exibidos: {group.totalMatching}</span>
                            <span
                              className={`text-lg font-semibold text-slate-400 transition-transform ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            >
                              ▾
                            </span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="divide-y divide-slate-100">
                            {group.fields
                              .slice()
                              .sort((a, b) => {
                                if (a.isActive === b.isActive) {
                                  return a.folder.fieldName.localeCompare(b.folder.fieldName);
                                }
                                return a.isActive ? -1 : 1;
                              })
                              .map((field) => (
                                <div key={field.folder.id} className="space-y-3 bg-white px-4 py-4">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <span className="text-sm font-semibold text-slate-600">
                                      {field.folder.fieldName}
                                    </span>
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                                      {field.totalMatching} exibidos de {field.totalFiles}
                                    </span>
                                  </div>
                                  {field.files.length > 0 ? (
                                    <ul className="space-y-2">
                                      {field.files.map((file) => (
                                        <li
                                          key={file.relativePath}
                                          className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                                        >
                                          <div className="flex items-center gap-3">
                                            <FaFileAlt className="text-slate-400" />
                                            <div className="flex min-w-0 flex-col">
                                              <span className="truncate font-medium text-slate-700" title={file.name}>
                                                {file.name}
                                              </span>
                                              <div className="flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
                                                <span>{formatBytes(file.size)}</span>
                                                <span>{file.monthName ? `${file.monthName} / ${file.year}` : file.year}</span>
                                                <span>{formatDateTime(file.modifiedAt)}</span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-end gap-2">
                                            <TranslationSafeIconButton
                                              type="button"
                                              onClick={() => handleDownloadFile(file)}
                                              disabled={downloadingKey === file.relativePath}
                                              icon={FaDownload}
                                              label={
                                                downloadingKey === file.relativePath ? "Baixando..." : "Baixar"
                                              }
                                              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                                                downloadingKey === file.relativePath
                                                  ? "cursor-not-allowed bg-blue-300"
                                                  : "bg-blue-600 hover:bg-blue-700"
                                              }`}
                                            />
                                            {canManageAccess && (
                                              <TranslationSafeIconButton
                                                type="button"
                                                onClick={() => handleDeleteFile(file)}
                                                disabled={deletingKey === file.relativePath}
                                                icon={FaTrashAlt}
                                                label={
                                                  deletingKey === file.relativePath ? "Excluindo..." : "Excluir"
                                                }
                                                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-red-200 ${
                                                  deletingKey === file.relativePath
                                                    ? "cursor-not-allowed bg-red-300"
                                                    : "bg-red-600 hover:bg-red-700"
                                                }`}
                                              />
                                            )}
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-xs text-slate-400">
                                      Nenhum anexo corresponde aos filtros informados para este campo.
                                    </p>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : folderFiles.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center text-slate-500">
                  Nenhum anexo corresponde aos filtros informados.
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Arquivo</th>
                        <th className="px-4 py-3 text-left font-semibold">Solicitação</th>
                        <th className="px-4 py-3 text-left font-semibold">Período</th>
                        <th className="px-4 py-3 text-left font-semibold">Atualizado</th>
                        <th className="px-4 py-3 text-right font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {folderFiles.map((file) => (
                        <tr key={file.relativePath} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <FaFileAlt className="text-slate-400" />
                              <div className="flex min-w-0 flex-col">
                                <span className="truncate font-medium text-slate-700" title={file.name}>
                                  {file.name}
                                </span>
                                <span className="text-xs text-slate-400">{formatBytes(file.size)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {file.request ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-700">{file.request.label}</span>
                                <span className="text-xs text-slate-400">{file.request.sector || file.request.requester ? [file.request.requester, file.request.sector].filter(Boolean).join(" • ") : ""}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">Sem identificação da solicitação</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {file.monthName ? `${file.monthName} / ${file.year}` : file.year}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {formatDateTime(file.modifiedAt)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <TranslationSafeIconButton
                                type="button"
                                onClick={() => handleDownloadFile(file)}
                                disabled={downloadingKey === file.relativePath}
                                icon={FaDownload}
                                label={downloadingKey === file.relativePath ? "Baixando..." : "Baixar"}
                                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                                  downloadingKey === file.relativePath
                                    ? "cursor-not-allowed bg-blue-300"
                                    : "bg-blue-600 hover:bg-blue-700"
                                }`}
                              />
                              {canManageAccess && (
                                <TranslationSafeIconButton
                                  type="button"
                                  onClick={() => handleDeleteFile(file)}
                                  disabled={deletingKey === file.relativePath}
                                  icon={FaTrashAlt}
                                  label={deletingKey === file.relativePath ? "Excluindo..." : "Excluir"}
                                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-red-200 ${
                                    deletingKey === file.relativePath
                                      ? "cursor-not-allowed bg-red-300"
                                      : "bg-red-600 hover:bg-red-700"
                                  }`}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
